import * as React from 'react';
import { Error, CheckCircle } from '@mui/icons-material';
import colors from '../../../../config/theme/colors';
import { Container, Item } from 'Components/Grid';
import { MyServiceContext } from '..';
import HistoryTabs from 'Components/HistoryTabs';
import TaskListHistory from './TaskListHistory';
import { useAppSelector as useSelector } from 'Store/index';
import SurveysHistory from './SurveysHistory';
import { dateFromPartial } from 'Components/DashboardGraph/Utils';
import { nowPartialDate } from 'neuro-utils';
import { getMySQUsageStatus, TSurveyType } from 'Routes/MyService/util';
import InvitesHistory from './InvitesHistory';
import { equals } from 'ramda';
import { ICompactVaultUser } from 'neuro-data-structures';
import { informantSurveysByPlatform, surveysByPlatform } from '../config';
import SuppliesHistory from './SuppliesHistory';
import MyServiceAccess from './components/MyServiceAccess';

const PatientMyServiceStatus = ({
  fm,
  platform,
}: {
  fm: (id: string) => string | React.ReactNode;
  platform: Platform | undefined;
}): React.JSX.Element => {
  const patientMySQStatus = useSelector((s: IState) => s.myapp.mysqUserId);
  const delegates = useSelector((s: IState) => s.patient.delegates);
  const mysqStatus = getMySQUsageStatus(patientMySQStatus, delegates);

  return (
    <Container style={{ marginBottom: platform === 'ms' ? (mysqStatus ? '2.5rem' : '5rem') : '3.2rem' }}>
      <Item xs={5}>
        {!mysqStatus ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Error style={{ color: colors.warning.default, margin: '0 0.5rem 0 0.3rem' }} />
              <span style={{ color: colors.tertiaryText, fontWeight: 600 }}>
                {fm('myService.patientNotUsingMyService')}
              </span>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle style={{ color: '#7BA015', margin: '0 0.5rem 0 0.3rem' }} />
            <span style={{ color: colors.tertiaryText, fontWeight: 600 }}>
              {fm(`myService.${mysqStatus}UsingMyService`)}
            </span>
          </div>
        )}
      </Item>
      <Item xs={7} style={platform === 'ms' ? { paddingLeft: '2rem' } : { justifyItems: 'flex-end' }}>
        <MyServiceAccess platform={platform} />
      </Item>
    </Container>
  );
};

const MyServiceHistory = ({ selectedTab, setSelectedTab, users, inviteErrors }: IOwnProps) => {
  const myServContext = React.useContext(MyServiceContext);
  const { fm, platform } = myServContext;

  const [onGoingTasks, setOnGoingTasks] = React.useState<Array<ITaskList>>([]);

  const taskLists = useSelector((s: IState) => s.myapp.taskLists) || [];
  const invites = useSelector((s: IState) => s.myapp.sentInvites);
  const sortedAndMergedDocs = useSelector((s: IState) => s.documents.sortedAndMergedDocuments) || [];
  const sortedAndMergedMyDocs = useSelector((s: IState) => s.myapp.sortedAndMergedDocuments) || [];

  React.useEffect(() => {
    const ongTasks = taskLists.filter(
      tl => tl.deadline && dateFromPartial(tl.deadline) > dateFromPartial(nowPartialDate()),
    );
    if (!equals(onGoingTasks, ongTasks)) setOnGoingTasks(ongTasks);
  }, [taskLists]);

  React.useEffect(() => {
    if (selectedTab >= tabs.length) {
      setSelectedTab(0);
    }
  }, [platform]);

  const treatmentDecisionDocs: ININMTTreatmentPeriod[] = sortedAndMergedDocs.filter(
    d => d._type === 'ninmtTreatmentPeriod',
  );

  const surveyDocs: Array<TSurveyType> = sortedAndMergedMyDocs.filter(
    d =>
      platform &&
      (surveysByPlatform[platform]
        ?.filter(t => (platform === 'ninmt' ? t !== 'supplyShortagesInquiry' : t))
        ?.includes(d._type) ||
        informantSurveysByPlatform[platform]?.includes(d._type)) &&
      (d._type.toLowerCase().includes('background') || d._type === 'parkinsonLifestyle' ? false : true),
  );

  const suppliesDocs: Array<TSurveyType> = sortedAndMergedMyDocs.filter(
    d => platform === 'ninmt' && d._type === 'supplyShortagesInquiry',
  );

  const tabs = [
    {
      title: 'myService.surveys',
      content: (
        <SurveysHistory
          treatmentDecisionDocs={platform === 'ninmt' ? treatmentDecisionDocs : []}
          surveyDocs={surveyDocs}
          onGoingTasks={onGoingTasks}
          users={users}
          taskLists={taskLists}
        />
      ),
    },
    {
      title: 'myService.taskLists',
      content: <TaskListHistory treatmentDecisionDocs={treatmentDecisionDocs} taskLists={taskLists} />,
    },
    {
      title: 'myService.invites',
      content: (
        <InvitesHistory
          invites={invites}
          inviteErrors={inviteErrors}
          taskLists={taskLists.filter(tl => tl.task_kind === 'PROM' || (!tl.task_kind && !tl.informantId))}
        />
      ),
    },
    {
      title: 'myService.supplies',
      content: <SuppliesHistory supplyShortagesInquiryDocs={suppliesDocs} users={users} />,
    },
  ].slice(
    ['ninmt', 'sleepApnea', 'sma', 'dmd', 'parkinson', 'mgravis'].includes(platform ?? '')
      ? 0
      : platform === 'ms'
        ? 1
        : 2,
    platform === 'ninmt' ? undefined : 3,
  );

  return (
    <React.Fragment>
      <PatientMyServiceStatus platform={platform} fm={fm} />
      <HistoryTabs
        indexSelectionTools={{
          index: selectedTab,
          changeFunction: (i: number): void => setSelectedTab(i),
        }}
      >
        {tabs}
      </HistoryTabs>
    </React.Fragment>
  );
};

interface IOwnProps {
  selectedTab: number;
  setSelectedTab: (tab: number) => void;
  users: ICompactVaultUser[];
  inviteErrors: Array<{ method: 'sms' | 'email'; statusCode: number }>;
}

export default MyServiceHistory;
